import React, { useEffect } from 'react';
import { Search, Handshake, BookOpen, ShieldCheck, Sparkles, X } from 'lucide-react';

interface Props {
  open: boolean;
  onClose: () => void;
  showHideToday?: boolean;
}

const ProspectiveStudentsModal: React.FC<Props> = ({ open, onClose, showHideToday = false }) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  const hideToday = () => {
    const expiry = new Date();
    expiry.setHours(24, 0, 0, 0);
    localStorage.setItem('chair_prospective_hide_until', String(expiry.getTime()));
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="relative bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 rounded-3xl p-1 shadow-2xl max-w-5xl w-full my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          aria-label="닫기"
          className="absolute -top-3 -right-3 z-10 bg-white text-gray-700 rounded-full p-2 shadow-lg hover:bg-gray-100 transition-colors"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="bg-white rounded-[22px] p-6 md:p-10 max-h-[85vh] overflow-y-auto">
          <div className="flex justify-center mb-5">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-indigo-100 to-purple-100 text-indigo-700 px-4 py-1.5 rounded-full text-sm font-semibold">
              <Sparkles className="h-4 w-4" />
              대학(원) 신입생들에게
            </div>
          </div>

          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-3 leading-tight">
              AI 시대, <span className="text-indigo-600">어떤 대학</span>을 선택해야 할까?
            </h2>
            <p className="text-base md:text-lg text-gray-600">
              "AI 시대, <span className="font-semibold text-gray-800">인간의 속도</span>로 나아가다"
            </p>
          </div>

          <div className="max-w-3xl mx-auto space-y-4 text-gray-700 leading-relaxed mb-8 text-sm md:text-base">
            <p>
              서울여자대학교의 <strong className="text-indigo-700">인간-AI 협력 연구센터(CHAIR)</strong>는
              <strong> 사람이 중심이 되는 AI 협력</strong>을 연구하고 있어요.
            </p>
            <p>
              서울여대가 오랫동안 지켜온 <strong>따뜻한 가르침의 전통</strong>을 AI 시대에 맞게 새롭게 이어가며,
              학생들이 AI를 <em>두려워하거나 무작정 쫓아가지 않고</em>,
              자신만의 시선으로 이해하고 활용할 수 있도록 돕고 있어요.
            </p>
            <p>
              서울여대에 입학하면 누구나 CHAIR가 운영하는
              <strong className="text-purple-700"> AI 특강, 국제 포럼, 공모전, AI 리터러시 교육</strong>에 참여할 수 있어요.
              AI를 똑똑하게 쓰는 법부터, AI와 함께 더 나은 미래를 만드는 법까지 — 선배·교수님과 함께 배워갈 수 있어요.
            </p>
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border-l-4 border-indigo-500 rounded-r-xl p-4 md:p-5 my-5">
              <p className="text-gray-800 font-medium leading-relaxed">
                AI 시대에 정말 필요한 사람은 기술을 빠르게 쫓아가는 사람이 아니라,
                <br className="hidden md:block" />
                <strong className="text-indigo-700">좋은 질문을 던지고 세상을 따뜻하게 바라볼 줄 아는 사람</strong>이에요.
              </p>
              <p className="text-gray-600 text-sm mt-2">
                서울여대 CHAIR는 그런 여러분이 멋지게 자라나는 자리에 늘 함께합니다.
              </p>
            </div>
          </div>

          <h3 className="text-xl md:text-2xl font-bold text-gray-900 text-center mb-2">
            CHAIR의 <span className="text-indigo-600">4개 연구 분과</span>
          </h3>
          <p className="text-center text-gray-500 mb-6 text-xs md:text-sm">
            인식 · 협력 · 교육 · 윤리 — 네 개의 축이 함께 만드는 인간-AI 협력
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card index="01" color="blue" Icon={Search} name="인식 분과" en="AWARENESS" catch="&quot;AI, 너는 누구니?&quot;"
              desc="AI가 어떻게 움직이는지, 어떤 정보를 모으는지 차근차근 알아가요. 잘 알아야 잘 쓸 수 있으니까요."
              keywords={['AI 리터러시', '데이터', '디지털 시민']} />
            <Card index="02" color="purple" Icon={Handshake} name="협력 분과" en="COLLABORATION" catch="&quot;혼자보다 함께!&quot;"
              desc="기술, 인문, 사회가 모여 사람과 AI가 함께 만드는 미래를 그려요. 다양한 친구들과 함께 배우는 법을 연구합니다."
              keywords={['다학제 융합', '팀워크', '공동 창작']} />
            <Card index="03" color="amber" Icon={BookOpen} name="교육 분과" en="EDUCATION" catch="&quot;AI, 제대로 배워요!&quot;"
              desc="AI를 똑똑하게 활용하는 방법을 함께 배워요. 대학 공부가 AI 시대에 어떻게 더 즐겁고 의미 있어질지 고민합니다."
              keywords={['AI 리터러시 교육', '교육 헌장', '비판적 사고']} />
            <Card index="04" color="emerald" Icon={ShieldCheck} name="윤리 분과" en="ETHICS" catch="&quot;믿을 수 있는 AI를 위해&quot;"
              desc="말뿐인 약속이 아닌, 진짜 작동하는 AI 윤리를 만듭니다. 대학 안에서부터 안전하고 신뢰할 수 있는 AI 환경을 설계해요."
              keywords={['AI 윤리', '거버넌스', '책임', '안전']} />
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-8 pt-6 border-t border-gray-100">
            {showHideToday && (
              <button
                onClick={hideToday}
                className="text-sm text-gray-500 hover:text-gray-700 underline underline-offset-2"
              >
                오늘 하루 보지 않기
              </button>
            )}
            <button
              onClick={onClose}
              className="px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-medium rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all shadow-md"
            >
              닫기
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

interface CardProps {
  index: string;
  color: 'blue' | 'purple' | 'amber' | 'emerald';
  Icon: React.ComponentType<{ className?: string }>;
  name: string;
  en: string;
  catch: string;
  desc: string;
  keywords: string[];
}

const Card: React.FC<CardProps> = ({ index, color, Icon, name, en, catch: catchPhrase, desc, keywords }) => {
  const styles = {
    blue:    { bg: 'from-blue-50 to-indigo-50',     border: 'border-blue-100',    iconBg: 'bg-blue-500',    enText: 'text-blue-600',    catchText: 'text-indigo-700',  numText: 'text-blue-400',    tagText: 'text-blue-700',    tagBorder: 'border-blue-200',    divider: 'border-blue-100' },
    purple:  { bg: 'from-purple-50 to-pink-50',     border: 'border-purple-100',  iconBg: 'bg-purple-500',  enText: 'text-purple-600',  catchText: 'text-purple-700',  numText: 'text-purple-400',  tagText: 'text-purple-700',  tagBorder: 'border-purple-200',  divider: 'border-purple-100' },
    amber:   { bg: 'from-amber-50 to-orange-50',    border: 'border-amber-100',   iconBg: 'bg-amber-500',   enText: 'text-amber-600',   catchText: 'text-amber-700',   numText: 'text-amber-400',   tagText: 'text-amber-700',   tagBorder: 'border-amber-200',   divider: 'border-amber-100' },
    emerald: { bg: 'from-emerald-50 to-teal-50',    border: 'border-emerald-100', iconBg: 'bg-emerald-500', enText: 'text-emerald-600', catchText: 'text-emerald-700', numText: 'text-emerald-400', tagText: 'text-emerald-700', tagBorder: 'border-emerald-200', divider: 'border-emerald-100' },
  }[color];

  return (
    <div className={`relative bg-gradient-to-br ${styles.bg} rounded-2xl p-5 border ${styles.border} hover:shadow-lg transition-all`}>
      <div className={`absolute top-3 right-3 text-xs font-bold ${styles.numText}`}>{index}</div>
      <div className={`${styles.iconBg} w-11 h-11 rounded-xl flex items-center justify-center mb-3 shadow-md`}>
        <Icon className="h-5 w-5 text-white" />
      </div>
      <h4 className="font-bold text-gray-900 text-base mb-1">{name}</h4>
      <div className={`text-xs ${styles.enText} font-semibold mb-2 tracking-wider`}>{en}</div>
      <p className={`${styles.catchText} font-semibold text-sm mb-2`} dangerouslySetInnerHTML={{ __html: catchPhrase }} />
      <p className="text-gray-600 text-xs leading-relaxed mb-3">{desc}</p>
      <div className={`flex flex-wrap gap-1 pt-2.5 border-t ${styles.divider}`}>
        {keywords.map(kw => (
          <span key={kw} className={`text-[10px] bg-white ${styles.tagText} px-1.5 py-0.5 rounded-full border ${styles.tagBorder}`}>
            {kw}
          </span>
        ))}
      </div>
    </div>
  );
};

export default ProspectiveStudentsModal;
